/* eslint-disable react/jsx-props-no-spreading */
import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import {
  Drawer,
  Tabs,
} from 'antd';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import FilterViewTab from './FilterViewTab';
import { FOOTER_HEIGHT } from '../../constants/Styles';

const { TabPane } = Tabs;

export default function FilterDrawer({
  title,
  visible,
  onClose,
  filterViews,
  CustomFilterTab,
  drawerProps = {},
  activeFilters: propActiveFilters,
  usePropFilters,
  onFavourite,
  ...dataProps
}) {
  const reduxFilters = useSelector((state) => state.schedule.fieldScheduleFilters) || {};
  const activeFilters = usePropFilters ? propActiveFilters : reduxFilters;

  const [activeTab, setActiveTab] = useState('custom');

  // Sort filter views with the favourite first (only one can be favourite at a time)
  const sortedFilterViews = useMemo(() => {
    if (!filterViews) return [];
    return [...filterViews].sort((a, b) => {
      if (a.isFavourite) return -1;
      if (b.isFavourite) return 1;
      return 0;
    });
  }, [filterViews]);

  useEffect(() => {
    setActiveTab(activeFilters.filterView ?? activeFilters.id ?? 'custom');
  }, [activeFilters]);

  const onTabChange = useCallback((key) => {
    setActiveTab(key);
  }, [setActiveTab]);

  return (
    <Drawer
      title={title}
      visible={visible}
      onClose={onClose}
      width={900}
      destroyOnClose
      bodyStyle={{ marginBottom: FOOTER_HEIGHT, paddingBottom: 0 }}
      {...drawerProps}
    >
      <Tabs
        activeKey={activeTab}
        tabPosition="left"
        onChange={onTabChange}
        className="filter-drawer-tabs"
        destroyInactiveTabPane
      >
        <TabPane
          tab="Custom View"
          key="custom"
        >
          <CustomFilterTab
            onClose={onClose}
            setActiveTab={setActiveTab}
            activeFilters={activeFilters}
            {...dataProps}
          />
        </TabPane>
        {
          sortedFilterViews.map((view) => (
            <TabPane
              tab={<FilterViewTab view={view} onFavourite={onFavourite} />}
              key={view.id}
            >
              <CustomFilterTab
                onClose={onClose}
                filterView={view}
                setActiveTab={setActiveTab}
                activeFilters={activeFilters}
                {...dataProps}
              />
            </TabPane>
          ))
        }
      </Tabs>
    </Drawer>
  );
}

FilterDrawer.propTypes = {
  title: PropTypes.string,
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  filterViews: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      filterViewName: PropTypes.string,
      isFavourite: PropTypes.bool,
    }),
  ),
  dataProps: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    ),
  ),
  CustomFilterTab: PropTypes.node.isRequired,
  drawerProps: PropTypes.shape({}),
  activeFilters: PropTypes.shape({}),
  usePropFilters: PropTypes.bool,
  onFavourite: PropTypes.func,
};

FilterDrawer.defaultProps = {
  title: 'Filters',
  filterViews: [],
  dataProps: {},
  drawerProps: {},
  activeFilters: {},
  usePropFilters: false,
  onFavourite: null,
};
